/**
 * PCDrilldownDialog — Performance Centre KPI Drill-down
 *
 * Supports two record types returned by /api/reports/performance-overview/drilldown:
 *   - Jobs  (completed_jobs, backlog, aged_*, forecast_revenue)
 *   - Sites (overdue_sites, overdue_no_future, billing_at_risk)
 *
 * Row count reconciles exactly to the headline KPI shown on the PC card.
 */
import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const SITE_TYPES    = new Set(['overdue_sites', 'overdue_no_future', 'billing_at_risk']);
const FORECAST_TYPE = 'forecast_revenue';
const BILLING_TYPES = new Set(['billing_at_risk', 'overdue_sites', 'overdue_no_future']);

function fmt(d) {
  return d ? format(parseISO(d), 'd MMM yyyy') : '—';
}

function fmtCurrency(v) {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(v);
}

function JobRow({ r, showRevenue }) {
  const scheduledDate = r.scheduled_date
    ? (r.end_date && r.end_date !== r.scheduled_date
        ? `${format(parseISO(r.scheduled_date), 'd MMM')} – ${format(parseISO(r.end_date), 'd MMM yy')}`
        : format(parseISO(r.scheduled_date), 'd MMM yyyy'))
    : '—';

  const completionDate = r.completed_on || r.completed_at ? fmt(r.completed_on || r.completed_at) : null;

  const daysEl = r.days_overdue != null && r.days_overdue >= 0
    ? <span className={r.days_overdue >= 15 ? 'text-red-600 font-semibold' : r.days_overdue >= 8 ? 'text-orange-600 font-semibold' : 'text-amber-600'}>{r.days_overdue}d</span>
    : null;

  const auditors = (r.auditors || []).join(', ') || '—';

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors text-xs" data-testid={`pc-drilldown-job-${r.id}`}>
      <td className="px-2 py-1.5 whitespace-nowrap">{scheduledDate}</td>
      <td className="px-2 py-1.5">
        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
          r.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
        }`}>
          {r.status === 'COMPLETED' ? 'Completed' : 'Scheduled'}
        </span>
      </td>
      <td className="px-2 py-1.5">
        {r.client_name
          ? <span className="flex items-center gap-1">
              {r.client_colour && <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: r.client_colour }} />}
              <span className="truncate max-w-[120px]">{r.client_name}</span>
            </span>
          : '—'}
      </td>
      <td className="px-2 py-1.5 truncate max-w-[120px] text-slate-700">{r.site_name || '—'}</td>
      <td className="px-2 py-1.5 text-slate-500 truncate max-w-[130px]">{auditors}</td>
      {showRevenue && <td className="px-2 py-1.5 text-right font-mono text-emerald-700">{fmtCurrency(r.revenue)}</td>}
      <td className="px-2 py-1.5 text-right">
        {completionDate
          ? <span className="text-slate-500">{completionDate}</span>
          : (daysEl || <span className="text-slate-400">—</span>)}
      </td>
    </tr>
  );
}

function SiteRow({ r, showBilling }) {
  const lastAudit = r.last_audit_date ? format(parseISO(r.last_audit_date), 'd MMM yyyy') : 'Never';
  const nextAudit = r.next_planned_date ? format(parseISO(r.next_planned_date), 'd MMM yyyy') : null;
  // overdue_by_days = days_since - expected_interval (how long past the window, NOT raw days since visit).
  // Fall back to computing it client-side for any legacy records that don't have the field yet.
  const overdueBy = r.overdue_by_days != null
    ? r.overdue_by_days
    : (r.days_since_last_audit != null && r.expected_interval_days != null
        ? Math.max(0, r.days_since_last_audit - r.expected_interval_days)
        : r.days_since_last_audit);
  const overdueColour = overdueBy >= 30
    ? 'text-red-600'
    : overdueBy >= 14
    ? 'text-orange-600'
    : 'text-amber-600';
  const tooltip = [
    r.days_since_last_audit != null ? `Last visit: ${r.days_since_last_audit}d ago` : null,
    r.expected_interval_days != null ? `Visit every ${r.expected_interval_days}d` : null,
  ].filter(Boolean).join(' · ');
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors text-xs" data-testid={`pc-drilldown-site-${r.id}`}>
      <td className="px-2 py-1.5">
        {r.client_name
          ? <span className="flex items-center gap-1">
              {r.client_colour && <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: r.client_colour }} />}
              <span className="truncate max-w-[120px]">{r.client_name}</span>
            </span>
          : '—'}
      </td>
      <td className="px-2 py-1.5 font-medium text-slate-800 truncate max-w-[140px]">{r.site_name || '—'}</td>
      <td className="px-2 py-1.5 text-slate-600">{lastAudit}</td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        {r.frequency_label
          ? <span className="text-slate-500 text-[10px] bg-slate-100 px-1 py-0.5 rounded">{r.frequency_label}</span>
          : '—'}
      </td>
      <td className="px-2 py-1.5" title={tooltip || undefined}>
        {overdueBy != null
          ? <span className={`font-semibold ${overdueColour}`}>{overdueBy}d</span>
          : '—'}
      </td>
      {showBilling && (
        <td className="px-2 py-1.5 text-right font-mono text-orange-700">
          {r.billing_amount != null ? fmtCurrency(r.billing_amount) : '—'}
        </td>
      )}
      <td className="px-2 py-1.5">
        {nextAudit
          ? <span className="text-blue-600">{nextAudit}</span>
          : <span className="text-red-500 font-medium">None booked</span>}
      </td>
    </tr>
  );
}

export default function PCDrilldownDialog({ open, onOpenChange, title, description, type, records, loading, totalRevenue, totalBilling }) {
  const isSiteType    = SITE_TYPES.has(type);
  const showRevenue   = type === FORECAST_TYPE;
  const showBilling   = BILLING_TYPES.has(type);

  const totalRevenueDisplay = showRevenue && totalRevenue != null
    ? fmtCurrency(totalRevenue)
    : null;

  const totalBillingDisplay = showBilling && totalBilling != null
    ? fmtCurrency(totalBilling)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="overflow-y-auto"
        style={{ width: '60rem', maxWidth: '96vw', maxHeight: '90vh' }}
        data-testid="pc-drilldown-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {title}
            {!loading && (
              <span className="text-sm font-normal text-slate-500">
                ({records.length} {isSiteType ? 'site' : 'job'}{records.length !== 1 ? 's' : ''}
                {totalRevenueDisplay ? ` · ${totalRevenueDisplay}` : ''}
                {totalBillingDisplay ? ` · ${totalBillingDisplay}` : ''})
              </span>
            )}
          </DialogTitle>
          {description && <DialogDescription className="text-xs">{description}</DialogDescription>}
        </DialogHeader>

        <div className="mt-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-7 w-7 animate-spin text-slate-400" />
            </div>
          ) : records.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No records found.</p>
          ) : (
            <div className="overflow-auto max-h-[60vh]">
              <table className="w-full text-xs border-collapse min-w-[540px]">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr className="border-b border-slate-200">
                    {isSiteType ? (
                      <>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Client</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Site</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Last Audit</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Schedule</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Overdue By</th>
                        {showBilling && <th className="px-2 py-2 text-right font-semibold text-slate-600">Billing</th>}
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Next Planned</th>
                      </>
                    ) : (
                      <>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Scheduled</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Status</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Client</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Site</th>
                        <th className="px-2 py-2 text-left font-semibold text-slate-600">Auditors</th>
                        {showRevenue && <th className="px-2 py-2 text-right font-semibold text-slate-600">Revenue</th>}
                        <th className="px-2 py-2 text-right font-semibold text-slate-600">
                          {showRevenue || type === 'completed_jobs' ? 'Completed' : 'Overdue By'}
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {isSiteType
                    ? records.map((r) => <SiteRow key={r.id} r={r} showBilling={showBilling} />)
                    : records.map((r) => <JobRow key={r.id} r={r} showRevenue={showRevenue} />)}
                </tbody>
                {showRevenue && records.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                      <td className="px-2 py-2 text-slate-600" colSpan={5}>{records.length} jobs total</td>
                      <td className="px-2 py-2 text-right text-emerald-700">{totalRevenueDisplay}</td>
                      <td />
                    </tr>
                  </tfoot>
                )}
                {showBilling && records.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                      <td className="px-2 py-2 text-slate-600" colSpan={5}>{records.length} sites total</td>
                      <td className="px-2 py-2 text-right text-orange-700">{totalBillingDisplay}</td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
